import { supabase, SUPABASE_PAGE_SIZE } from '../supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef } from 'react';

export interface FanGainEntry {
  id: string;
  model_id: string;
  date: string;
  type: 'gg-swaps' | 'fan-gain';
  value: number;
  created_at: string;
  updated_at: string;
}

interface CreateFanGainEntryData {
  model_id: string;
  date: DateTime;
  type: 'gg-swaps' | 'fan-gain';
  value: number;
}

interface UpdateFanGainEntryData {
  model_id: string;
  date: DateTime;
  type: 'gg-swaps' | 'fan-gain';
  value: number;
}

interface DateRangeQuery {
  startDate: DateTime;
  endDate: DateTime;
  type?: 'gg-swaps' | 'fan-gain';
  model_id?: string;
}

interface MarkerDateRangeQuery {
  startDate: DateTime;
  endDate: DateTime;
  type: 'PROMO_CHANGE' | 'UNSYNCED';
  model_id?: string;
}

interface FanGainAssignment {
  id: string;
  user_id: string;
  model_id: string;
  index: number;
  created_at: string;
  updated_at: string;
}

interface FanGainUserOrder {
  id: string;
  user_id: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

interface RevenueEntry {
  id: string;
  model_id: string;
  date: string;
  revenue: number;
  created_at: string;
  updated_at: string;
}

interface CreateRevenueEntryData {
  model_id: string;
  date: DateTime;
  revenue: number;
}

interface UpdateRevenueEntryData {
  model_id: string;
  date: DateTime;
  revenue: number;
}

export interface FanGainMarker {
  id: string;
  model_id: string;
  date: string;
  type: 'PROMO_CHANGE' | 'UNSYNCED';
  created_at: string;
}

interface CreateFanGainMarkerData {
  model_id: string;
  date: DateTime;
  type: 'PROMO_CHANGE' | 'UNSYNCED';
}

const SupabaseFanGainService = {
  // Normalize date to 12pm UTC on the specified date
  normalizeDate(date: DateTime): string {
    const utcDate = date.set({ hour: 12, minute: 0, second: 0 });
    return utcDate.toISO() || '';
  },

  async createEntry(entryData: CreateFanGainEntryData): Promise<string> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { data, error } = await supabase
        .from('fan_gain_entries')
        .insert({
          model_id: entryData.model_id,
          date: normalizedDate,
          type: entryData.type,
          value: entryData.value
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating fan gain entry:', error);
      throw error;
    }
  },

  async updateEntry(entryData: UpdateFanGainEntryData): Promise<void> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { error } = await supabase
        .from('fan_gain_entries')
        .update({
          value: entryData.value,
          updated_at: DateTime.utc()
        })
        .eq('model_id', entryData.model_id)
        .eq('date', normalizedDate)
        .eq('type', entryData.type);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating fan gain entry:', error);
      throw error;
    }
  },

  async deleteEntry(model_id: string, date: DateTime, type: 'gg-swaps' | 'fan-gain'): Promise<void> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { error } = await supabase
        .from('fan_gain_entries')
        .delete()
        .eq('model_id', model_id)
        .eq('date', normalizedDate)
        .eq('type', type);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting fan gain entry:', error);
      throw error;
    }
  },

  async getEntry(model_id: string, date: DateTime, type: 'gg-swaps' | 'fan-gain'): Promise<FanGainEntry | null> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { data, error } = await supabase
        .from('fan_gain_entries')
        .select('*')
        .eq('model_id', model_id)
        .eq('date', normalizedDate)
        .eq('type', type)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting fan gain entry:', error);
      throw error;
    }
  },

  async getEntriesInDateRange(query: DateRangeQuery): Promise<FanGainEntry[]> {
    try {
      const startDate = SupabaseFanGainService.normalizeDate(query.startDate);
      const endDate = SupabaseFanGainService.normalizeDate(query.endDate);

      const MAX_CONCURRENT_PAGES = 10;

      // Build filters array for reuse
      const filters = [
        { field: 'date', operator: 'gte', value: startDate },
        { field: 'date', operator: 'lte', value: endDate }
      ];

      if (query.type) {
        filters.push({ field: 'type', operator: 'eq', value: query.type });
      }

      if (query.model_id) {
        filters.push({ field: 'model_id', operator: 'eq', value: query.model_id });
      }

      // Get total count first
      let countQuery = supabase.from('fan_gain_entries').select('*', { count: 'exact', head: true });

      filters.forEach(filter => {
        switch (filter.operator) {
          case 'eq': countQuery = countQuery.eq(filter.field, filter.value); break;
          case 'gte': countQuery = countQuery.gte(filter.field, filter.value); break;
          case 'lte': countQuery = countQuery.lte(filter.field, filter.value); break;
        }
      });

      const { count } = await countQuery;
      if (!count || count === 0) return [];

      // If count is under PAGE_SIZE, use single query
      if (count <= SUPABASE_PAGE_SIZE) {
        let queryBuilder = supabase
          .from('fan_gain_entries')
          .select('*');

        filters.forEach(filter => {
          switch (filter.operator) {
            case 'eq': queryBuilder = queryBuilder.eq(filter.field, filter.value); break;
            case 'gte': queryBuilder = queryBuilder.gte(filter.field, filter.value); break;
            case 'lte': queryBuilder = queryBuilder.lte(filter.field, filter.value); break;
          }
        });

        const { data, error } = await queryBuilder
          .order('date', { ascending: true })
          .order('model_id', { ascending: true });

        if (error) throw error;
        return data || [];
      }

      // Use parallel pagination for large datasets
      const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);
      const allResults: FanGainEntry[] = [];

      for (let batchStart = 0; batchStart < totalPages; batchStart += MAX_CONCURRENT_PAGES) {
        const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PAGES, totalPages);
        const pagePromises: Promise<FanGainEntry[]>[] = [];

        for (let page = batchStart; page < batchEnd; page++) {
          const pagePromise = (async (offset: number): Promise<FanGainEntry[]> => {
            let queryBuilder = supabase.from('fan_gain_entries').select('*');

            // Apply filters
            filters.forEach(filter => {
              switch (filter.operator) {
                case 'eq': queryBuilder = queryBuilder.eq(filter.field, filter.value); break;
                case 'gte': queryBuilder = queryBuilder.gte(filter.field, filter.value); break;
                case 'lte': queryBuilder = queryBuilder.lte(filter.field, filter.value); break;
              }
            });

            // Apply sorting with stable secondary sort
            queryBuilder = queryBuilder
              .order('date', { ascending: true })
              .order('model_id', { ascending: true })
              .order('id', { ascending: true });

            // Add pagination
            queryBuilder = queryBuilder.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

            const { data, error } = await queryBuilder;
            if (error) throw error;

            return (data || []) as FanGainEntry[];
          })(page * SUPABASE_PAGE_SIZE);

          pagePromises.push(pagePromise);
        }

        // Execute batch in parallel
        const batchResults = await Promise.all(pagePromises);
        allResults.push(...batchResults.flat());
      }

      return allResults;
    } catch (error) {
      console.error('Error getting fan gain entries in date range:', error);
      throw error;
    }
  },

  async getEntriesByType(type: 'gg-swaps' | 'fan-gain', startDate: DateTime, endDate: DateTime): Promise<FanGainEntry[]> {
    return SupabaseFanGainService.getEntriesInDateRange({ startDate, endDate, type });
  },

  async getEntriesByModelAndType(model_id: string, type: 'gg-swaps' | 'fan-gain', startDate: DateTime, endDate: DateTime): Promise<FanGainEntry[]> {
    return SupabaseFanGainService.getEntriesInDateRange({ startDate, endDate, type, model_id });
  },

  async upsertEntry(entryData: UpdateFanGainEntryData): Promise<string> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { data, error } = await supabase
        .from('fan_gain_entries')
        .upsert({
          model_id: entryData.model_id,
          date: normalizedDate,
          type: entryData.type,
          value: entryData.value,
          updated_at: DateTime.utc()
        }, {
          onConflict: 'date,type,model_id'
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error upserting fan gain entry:', error);
      throw error;
    }
  },

  async batchUpsertEntries(entries: UpdateFanGainEntryData[]): Promise<void> {
    try {
      const normalizedEntries = entries.map(entry => ({
        model_id: entry.model_id,
        date: SupabaseFanGainService.normalizeDate(entry.date),
        type: entry.type,
        value: entry.value,
        updated_at: DateTime.utc()
      }));

      const { error } = await supabase
        .from('fan_gain_entries')
        .upsert(normalizedEntries, {
          onConflict: 'date,type,model_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error batch upserting fan gain entries:', error);
      throw error;
    }
  },

  async getEntriesForMonth(year: number, month: number, type: 'gg-swaps' | 'fan-gain'): Promise<FanGainEntry[]> {
    try {
      const startDate = DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }).startOf("month");
      const endDate = startDate.endOf("month");

      return SupabaseFanGainService.getEntriesInDateRange({ startDate, endDate, type });
    } catch (error) {
      console.error('Error getting entries for month:', error);
      throw error;
    }
  },

  async entryExists(model_id: string, date: DateTime, type: 'gg-swaps' | 'fan-gain'): Promise<boolean> {
    try {
      const entry = await SupabaseFanGainService.getEntry(model_id, date, type);
      return entry !== null;
    } catch (error) {
      console.error('Error checking if entry exists:', error);
      return false;
    }
  },

  async getFanGainAssignments(): Promise<FanGainAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_assignments')
        .select('*')
        .order('index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting fan gain assignments:', error);
      throw error;
    }
  },

  async getFanGainUserOrder(): Promise<FanGainUserOrder[]> {
    try {
      const { data, error } = await supabase
        .from('fan_gain_user_order')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting fan gain user order:', error);
      throw error;
    }
  },

  async getRevenueData(month: number, year: number): Promise<{ [model_id: string]: number }> {
    try {
      const startDate = DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }).startOf("month");
      const endDate = startDate.endOf("month");

      const { data, error } = await supabase
        .from('revenue_entries')
        .select('*')
        .gte('date', SupabaseFanGainService.normalizeDate(startDate))
        .lte('date', SupabaseFanGainService.normalizeDate(endDate));

      if (error) throw error;

      const revenueData: { [model_id: string]: number } = {};
      (data || []).forEach(entry => {
        revenueData[entry.model_id] = entry.revenue;
      });

      return revenueData;
    } catch (error) {
      console.error('Error getting revenue data:', error);
      throw error;
    }
  },

  async saveRevenueData(month: number, year: number, model_id: string, revenue: number): Promise<string> {
    try {
      const date = DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }).startOf("month");
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { data, error } = await supabase
        .from('revenue_entries')
        .upsert({
          model_id: model_id,
          date: normalizedDate,
          revenue: revenue,
          updated_at: DateTime.now()
        }, {
          onConflict: 'model_id,date'
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error saving revenue data:', error);
      throw error;
    }
  },

  async createRevenueEntry(entryData: CreateRevenueEntryData): Promise<string> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { data, error } = await supabase
        .from('revenue_entries')
        .insert({
          model_id: entryData.model_id,
          date: normalizedDate,
          revenue: entryData.revenue
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating revenue entry:', error);
      throw error;
    }
  },

  async updateRevenueEntry(entryData: UpdateRevenueEntryData): Promise<void> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { error } = await supabase
        .from('revenue_entries')
        .update({
          revenue: entryData.revenue,
          updated_at: DateTime.now()
        })
        .eq('model_id', entryData.model_id)
        .eq('date', normalizedDate);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating revenue entry:', error);
      throw error;
    }
  },

  async deleteRevenueEntry(model_id: string, date: DateTime): Promise<void> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { error } = await supabase
        .from('revenue_entries')
        .delete()
        .eq('model_id', model_id)
        .eq('date', normalizedDate);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting revenue entry:', error);
      throw error;
    }
  },

  async getRevenueEntry(model_id: string, date: DateTime): Promise<RevenueEntry | null> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { data, error } = await supabase
        .from('revenue_entries')
        .select('*')
        .eq('model_id', model_id)
        .eq('date', normalizedDate)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting revenue entry:', error);
      throw error;
    }
  },

  async upsertRevenueEntry(entryData: UpdateRevenueEntryData): Promise<string> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(entryData.date);

      const { data, error } = await supabase
        .from('revenue_entries')
        .upsert({
          model_id: entryData.model_id,
          date: normalizedDate,
          revenue: entryData.revenue,
          updated_at: DateTime.now()
        }, {
          onConflict: 'model_id,date'
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error upserting revenue entry:', error);
      throw error;
    }
  },

  async createMarker(markerData: CreateFanGainMarkerData): Promise<string> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(markerData.date);

      const { data, error } = await supabase
        .from('fan_gain_markers')
        .insert({
          model_id: markerData.model_id,
          date: normalizedDate,
          type: markerData.type
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating fan gain marker:', error);
      throw error;
    }
  },

  async deleteMarker(model_id: string, date: DateTime, type: 'PROMO_CHANGE' | 'UNSYNCED'): Promise<void> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { error } = await supabase
        .from('fan_gain_markers')
        .delete()
        .eq('model_id', model_id)
        .eq('date', normalizedDate)
        .eq('type', type);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting fan gain marker:', error);
      throw error;
    }
  },

  async getMarker(model_id: string, date: DateTime, type: 'PROMO_CHANGE' | 'UNSYNCED'): Promise<FanGainMarker | null> {
    try {
      const normalizedDate = SupabaseFanGainService.normalizeDate(date);

      const { data, error } = await supabase
        .from('fan_gain_markers')
        .select('*')
        .eq('model_id', model_id)
        .eq('date', normalizedDate)
        .eq('type', type)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error getting fan gain marker:', error);
      throw error;
    }
  },

  async getMarkersInDateRange(query: MarkerDateRangeQuery): Promise<FanGainMarker[]> {
    try {
      const startDate = SupabaseFanGainService.normalizeDate(query.startDate);
      const endDate = SupabaseFanGainService.normalizeDate(query.endDate);

      const PAGE_SIZE = 1000;
      const MAX_CONCURRENT_PAGES = 10;

      // Build filters array for reuse
      const filters = [
        { field: 'date', operator: 'gte', value: startDate },
        { field: 'date', operator: 'lte', value: endDate }
      ];

      if (query.type) {
        filters.push({ field: 'type', operator: 'eq', value: query.type });
      }

      if (query.model_id) {
        filters.push({ field: 'model_id', operator: 'eq', value: query.model_id });
      }

      // Get total count first
      let countQuery = supabase.from('fan_gain_markers').select('*', { count: 'exact', head: true });

      filters.forEach(filter => {
        switch (filter.operator) {
          case 'eq': countQuery = countQuery.eq(filter.field, filter.value); break;
          case 'gte': countQuery = countQuery.gte(filter.field, filter.value); break;
          case 'lte': countQuery = countQuery.lte(filter.field, filter.value); break;
        }
      });

      const { count } = await countQuery;
      if (!count || count === 0) return [];

      // If count is under PAGE_SIZE, use single query
      if (count <= PAGE_SIZE) {
        let queryBuilder = supabase
          .from('fan_gain_markers')
          .select('*');

        filters.forEach(filter => {
          switch (filter.operator) {
            case 'eq': queryBuilder = queryBuilder.eq(filter.field, filter.value); break;
            case 'gte': queryBuilder = queryBuilder.gte(filter.field, filter.value); break;
            case 'lte': queryBuilder = queryBuilder.lte(filter.field, filter.value); break;
          }
        });

        const { data, error } = await queryBuilder
          .order('date', { ascending: true })
          .order('model_id', { ascending: true });

        if (error) throw error;
        return data || [];
      }

      // Use parallel pagination for large datasets
      const totalPages = Math.ceil(count / PAGE_SIZE);
      const allResults: FanGainMarker[] = [];

      for (let batchStart = 0; batchStart < totalPages; batchStart += MAX_CONCURRENT_PAGES) {
        const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PAGES, totalPages);
        const pagePromises: Promise<FanGainMarker[]>[] = [];

        for (let page = batchStart; page < batchEnd; page++) {
          const pagePromise = (async (offset: number): Promise<FanGainMarker[]> => {
            let queryBuilder = supabase.from('fan_gain_markers').select('*');

            // Apply filters
            filters.forEach(filter => {
              switch (filter.operator) {
                case 'eq': queryBuilder = queryBuilder.eq(filter.field, filter.value); break;
                case 'gte': queryBuilder = queryBuilder.gte(filter.field, filter.value); break;
                case 'lte': queryBuilder = queryBuilder.lte(filter.field, filter.value); break;
              }
            });

            // Apply sorting with stable secondary sort
            queryBuilder = queryBuilder
              .order('date', { ascending: true })
              .order('model_id', { ascending: true })
              .order('id', { ascending: true });

            // Add pagination
            queryBuilder = queryBuilder.range(offset, offset + PAGE_SIZE - 1);

            const { data, error } = await queryBuilder;
            if (error) throw error;

            return (data || []) as FanGainMarker[];
          })(page * PAGE_SIZE);

          pagePromises.push(pagePromise);
        }

        // Execute batch in parallel
        const batchResults = await Promise.all(pagePromises);
        allResults.push(...batchResults.flat());
      }

      return allResults;
    } catch (error) {
      console.error('Error getting fan gain markers in date range:', error);
      throw error;
    }
  },

  async getMarkersByType(type: 'PROMO_CHANGE' | 'UNSYNCED', startDate: DateTime, endDate: DateTime): Promise<FanGainMarker[]> {
    return SupabaseFanGainService.getMarkersInDateRange({ startDate, endDate, type });
  },

  async getMarkersByModelAndType(model_id: string, type: 'PROMO_CHANGE' | 'UNSYNCED', startDate: DateTime, endDate: DateTime): Promise<FanGainMarker[]> {
    return SupabaseFanGainService.getMarkersInDateRange({ startDate, endDate, type, model_id });
  },

  async toggleMarker(model_id: string, date: DateTime, type: 'PROMO_CHANGE' | 'UNSYNCED'): Promise<boolean> {
    try {
      const existingMarker = await SupabaseFanGainService.getMarker(model_id, date, type);

      if (existingMarker) {
        await SupabaseFanGainService.deleteMarker(model_id, date, type);
        return false;
      } else {
        await SupabaseFanGainService.createMarker({ model_id, date, type });
        return true;
      }
    } catch (error) {
      console.error(`Error toggling ${type} marker:`, error);
      throw error;
    }
  }
};

// Realtime subscription manager
export interface RealtimeFanGainManagerOptions {
  enabled?: boolean;
  dateRange?: { startDate: DateTime; endDate: DateTime };
  model_id?: string;
  type?: 'gg-swaps' | 'fan-gain';
  ignoreuser_id?: string;
  onError?: (error: any) => void;
  // Optional callbacks so UIs can react incrementally
  onInsert?: (entry: FanGainEntry) => void;
  onUpdate?: (entry: FanGainEntry) => void;
  onDelete?: (entry: FanGainEntry) => void;
}

export const useRealtimeFanGainManager = (options: RealtimeFanGainManagerOptions = {}) => {
  const { enabled = true, model_id, ignoreuser_id, onError, onInsert, onUpdate, onDelete } = options;
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);
  const lastLocalUpdateRef = useRef<number>(0);

  const shouldIncludeEntryInQuery = useCallback((entry: FanGainEntry, queryKey: readonly unknown[]): boolean => {
    const queryParams = queryKey[1] as any;
    if (!queryParams?.filters) return true;

    return queryParams.filters.every((filter: any) => {
      const { field, operator, value } = filter;
      const entryValue = entry[field as keyof FanGainEntry];

      switch (operator) {
        case 'eq':
          return entryValue === value;
        case 'neq':
          return entryValue !== value;
        case 'gt':
          return entryValue > value;
        case 'gte':
          return entryValue >= value;
        case 'lt':
          return entryValue < value;
        case 'lte':
          return entryValue <= value;
        case 'in':
          return Array.isArray(value) && value.includes(entryValue);
        case 'not-in':
          return Array.isArray(value) && !value.includes(entryValue);
        default:
          return true;
      }
    });
  }, []);

  const addNewEntryToQueries = useCallback((newRecord: any) => {
    const fanGainQueries = queryClient.getQueriesData<FanGainEntry[]>({
      queryKey: ['fan-gain-entries']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('New fan gain entry received:', { recordId: newRecord?.id });
    }

    fanGainQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: FanGainEntry[] | undefined) => {
        if (!currentData) return currentData;

        if (shouldIncludeEntryInQuery(newRecord, queryKey)) {
          const exists = currentData.some(entry => entry.id === newRecord.id);
          if (!exists) {
            return [...currentData, newRecord].sort((a, b) =>
              DateTime.fromISO(a.date, { zone: 'UTC' }).toMillis() - DateTime.fromISO(b.date, { zone: 'UTC' }).toMillis()
            );
          }
        }
        return currentData;
      });
    });
  }, [queryClient, shouldIncludeEntryInQuery]);

  const updateEntryInQueries = useCallback((updatedRecord: any) => {
    const fanGainQueries = queryClient.getQueriesData<FanGainEntry[]>({
      queryKey: ['fan-gain-entries']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Fan gain entry updated:', { recordId: updatedRecord?.id });
    }

    fanGainQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: FanGainEntry[] | undefined) => {
        if (!currentData) return currentData;

        if (shouldIncludeEntryInQuery(updatedRecord, queryKey)) {
          const existingIndex = currentData.findIndex(entry => entry.id === updatedRecord.id);
          if (existingIndex >= 0) {
            const newData = [...currentData];
            newData[existingIndex] = updatedRecord;
            return newData.sort((a, b) => DateTime.fromISO(a.date, { zone: 'UTC' }).toMillis() - DateTime.fromISO(b.date, { zone: 'UTC' }).toMillis());
          } else {
            return [...currentData, updatedRecord].sort((a, b) =>
              DateTime.fromISO(a.date, { zone: 'UTC' }).toMillis() - DateTime.fromISO(b.date, { zone: 'UTC' }).toMillis()
            );
          }
        } else {
          return currentData.filter(entry => entry.id !== updatedRecord.id);
        }
      });
    });
  }, [queryClient, shouldIncludeEntryInQuery]);

  const removeEntryFromQueries = useCallback((deletedRecord: any) => {
    const fanGainQueries = queryClient.getQueriesData<FanGainEntry[]>({
      queryKey: ['fan-gain-entries']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Fan gain entry deleted:', { recordId: deletedRecord?.id });
    }

    fanGainQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: FanGainEntry[] | undefined) => {
        if (!currentData) return currentData;
        return currentData.filter(entry => entry.id !== deletedRecord.id);
      });
    });
  }, [queryClient]);

  useEffect(() => {
    if (!enabled) return;

    const startSubscription = async () => {
      try {
        let channel = supabase
          .channel('fan-gain-entries-changes', { config: { private: true } })
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'fan_gain_entries',
            filter: model_id ? `model_id=eq.${model_id}` : undefined
          }, (payload) => {
            try {
              // Skip updates if they occurred shortly after a local update (debouncing)
              const now = DateTime.now().toMillis();
              if (now - lastLocalUpdateRef.current < 2000) {
                return;
              }

              if (payload.eventType === 'INSERT') {
                addNewEntryToQueries(payload.new);
                onInsert?.(payload.new as any);
              } else if (payload.eventType === 'UPDATE') {
                updateEntryInQueries(payload.new);
                onUpdate?.(payload.new as any);
              } else if (payload.eventType === 'DELETE') {
                removeEntryFromQueries(payload.old);
                onDelete?.(payload.old as any);
              }
            } catch (error) {
              console.error('Error processing fan gain entry change:', error);
              onError?.(error);
            }
          });

        channel.subscribe((status, err) => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Fan gain entries subscription status:', status);
          }
          if (err && onError) {
            onError(err);
          }
        });

        subscriptionRef.current = channel;
      } catch (error) {
        console.error('Error starting fan gain entries subscription:', error);
        onError?.(error);
      }
    };

    startSubscription();

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
        if (process.env.NODE_ENV !== 'production') {
          console.log('Unsubscribed from fan gain entries changes');
        }
      }
    };
  }, [enabled, model_id, ignoreuser_id, addNewEntryToQueries, updateEntryInQueries, removeEntryFromQueries, onError]);

  const markLocalUpdate = useCallback(() => {
    lastLocalUpdateRef.current = DateTime.now().toMillis();
  }, []);

  return {
    isSubscribed: !!subscriptionRef.current,
    markLocalUpdate
  };
};

// Query hooks
export const useFanGainEntry = (model_id: string, date: DateTime, type: 'gg-swaps' | 'fan-gain') => {
  const normalizedDate = SupabaseFanGainService.normalizeDate(date);
  return useQuery({
    queryKey: ['fan-gain-entries', {
      filters: [
        { field: 'model_id', operator: 'eq', value: model_id },
        { field: 'date', operator: 'eq', value: normalizedDate },
        { field: 'type', operator: 'eq', value: type }
      ]
    }],
    queryFn: () => SupabaseFanGainService.getEntry(model_id, date, type),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!model_id
  });
};

export const useFanGainEntriesInDateRange = (query: DateRangeQuery) => {
  const filters: any[] = [
    { field: 'date', operator: 'gte', value: SupabaseFanGainService.normalizeDate(query.startDate) },
    { field: 'date', operator: 'lte', value: SupabaseFanGainService.normalizeDate(query.endDate) }
  ];

  if (query.type) {
    filters.push({ field: 'type', operator: 'eq', value: query.type });
  }

  if (query.model_id) {
    filters.push({ field: 'model_id', operator: 'eq', value: query.model_id });
  }

  return useQuery({
    queryKey: ['fan-gain-entries', { filters }],
    queryFn: () => SupabaseFanGainService.getEntriesInDateRange(query),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!query.startDate && !!query.endDate
  });
};

export const useFanGainEntriesByType = (type: 'gg-swaps' | 'fan-gain', startDate: DateTime, endDate: DateTime) => {
  return useQuery({
    queryKey: ['fan-gain-entries', {
      filters: [
        { field: 'date', operator: 'gte', value: SupabaseFanGainService.normalizeDate(startDate) },
        { field: 'date', operator: 'lte', value: SupabaseFanGainService.normalizeDate(endDate) },
        { field: 'type', operator: 'eq', value: type }
      ]
    }],
    queryFn: () => SupabaseFanGainService.getEntriesByType(type, startDate, endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!type && !!startDate && !!endDate
  });
};

export const useFanGainEntriesByModelAndType = (model_id: string, type: 'gg-swaps' | 'fan-gain', startDate: DateTime, endDate: DateTime) => {
  return useQuery({
    queryKey: ['fan-gain-entries', {
      filters: [
        { field: 'date', operator: 'gte', value: SupabaseFanGainService.normalizeDate(startDate) },
        { field: 'date', operator: 'lte', value: SupabaseFanGainService.normalizeDate(endDate) },
        { field: 'type', operator: 'eq', value: type },
        { field: 'model_id', operator: 'eq', value: model_id }
      ]
    }],
    queryFn: () => SupabaseFanGainService.getEntriesByModelAndType(model_id, type, startDate, endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!model_id && !!type && !!startDate && !!endDate
  });
};

// Mutation hooks
export const useCreateFanGainEntry = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.createEntry
  });
};

export const useUpdateFanGainEntry = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.updateEntry
  });
};

export const useDeleteFanGainEntry = () => {
  return useMutation({
    mutationFn: ({ model_id, date, type }: { model_id: string; date: DateTime; type: 'gg-swaps' | 'fan-gain' }) =>
      SupabaseFanGainService.deleteEntry(model_id, date, type)
  });
};

export const useUpsertFanGainEntry = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.upsertEntry
  });
};

export const useBatchUpsertFanGainEntries = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.batchUpsertEntries
  });
};

export const useFanGainEntriesForMonth = (year: number, month: number, type: 'gg-swaps' | 'fan-gain') => {
  const startDate = DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }).startOf("month");
  const endDate = startDate.endOf("month");

  return useQuery({
    queryKey: ['fan-gain-entries', {
      filters: [
        { field: 'date', operator: 'gte', value: SupabaseFanGainService.normalizeDate(startDate) },
        { field: 'date', operator: 'lte', value: SupabaseFanGainService.normalizeDate(endDate) },
        { field: 'type', operator: 'eq', value: type }
      ]
    }],
    queryFn: () => SupabaseFanGainService.getEntriesForMonth(year, month, type),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!year && !!month && !!type
  });
};

export const useFanGainAssignments = () => {
  return useQuery({
    queryKey: ['fan-gain-assignments'],
    queryFn: SupabaseFanGainService.getFanGainAssignments,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000
  });
};

export const useFanGainUserOrder = () => {
  return useQuery({
    queryKey: ['fan-gain-user-order'],
    queryFn: SupabaseFanGainService.getFanGainUserOrder,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000
  });
};

export const useRevenueData = (month: number, year: number) => {
  return useQuery({
    queryKey: ['revenue-data', year, month],
    queryFn: () => SupabaseFanGainService.getRevenueData(month, year),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!month && !!year
  });
};

export const useRevenueEntry = (model_id: string, date: DateTime) => {
  return useQuery({
    queryKey: ['revenue-entry', model_id, SupabaseFanGainService.normalizeDate(date)],
    queryFn: () => SupabaseFanGainService.getRevenueEntry(model_id, date),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!model_id && !!date
  });
};

export const useCreateRevenueEntry = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.createRevenueEntry,
  });
};

export const useUpdateRevenueEntry = () => {
  return useMutation({
    mutationFn: SupabaseFanGainService.updateRevenueEntry,
  });
};

export const useDeleteRevenueEntry = () => {
  return useMutation({
    mutationFn: ({ model_id, date }: { model_id: string; date: DateTime }) =>
      SupabaseFanGainService.deleteRevenueEntry(model_id, date),
  });
};

export const useUpsertRevenueEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: SupabaseFanGainService.upsertRevenueEntry,
    onSuccess: (_, variables) => {
      const date = variables.date;
      const month = date.month;
      const year = date.year;

      queryClient.invalidateQueries({
        queryKey: ['revenue-data', year, month]
      });
      queryClient.invalidateQueries({
        queryKey: ['revenue-entry', variables.model_id, SupabaseFanGainService.normalizeDate(variables.date)]
      });
    }
  });
};

export const useSaveRevenueData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ month, year, model_id, revenue }: { month: number; year: number; model_id: string; revenue: number }) =>
      SupabaseFanGainService.saveRevenueData(month, year, model_id, revenue),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['revenue-data', variables.year, variables.month]
      });
      const date = DateTime.fromObject({ year: variables.year, month: variables.month, day: 1 }, { zone: 'UTC' });
      queryClient.invalidateQueries({
        queryKey: ['revenue-entry', variables.model_id, SupabaseFanGainService.normalizeDate(date)]
      });
    }
  });
};

export default SupabaseFanGainService;
